import { useMemo, useState } from "react";
import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

export default function CopyBomPicker({
    products = [],
    excludeProductId = "",
    excludeProductIds = [],
    selectedProductId = "",
    onSelect,
    label = "Copy BOM from…",
}) {
    const [search, setSearch] = useState("");
    const query = search.trim().toLowerCase();
    const options = useMemo(
        () =>
            (products || [])
                .filter(
                    (product) =>
                        String(product?.id || "") !== String(excludeProductId || "") &&
                        !(excludeProductIds || []).some((id) => String(id) === String(product?.id || ""))
                )
                .filter((product) => Array.isArray(product?.components) && product.components.length > 0)
                .filter((product) => {
                    if (!query) return true;
                    return [product?.supplySku, product?.sku, product?.canonicalName, product?.productName, product?.variantName]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase()
                        .includes(query);
                }),
        [excludeProductId, excludeProductIds, products, query]
    );
    const selected = options.find((product) => String(product.id) === String(selectedProductId));

    return (
        <div className="rounded-md border border-blue-100 bg-blue-50/50 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">{label}</p>
                    <p className="mt-0.5 text-[10px] text-blue-600">Copied lines stay editable after selection.</p>
                </div>
                {selected ? (
                    <span className="shrink-0 rounded-full bg-blue-100 px-2 py-1 text-[10px] font-semibold text-blue-700">
                        {selected.components.length} components
                    </span>
                ) : null}
            </div>
            <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search source SKU or product…"
                    className="h-8 w-full rounded-md border border-blue-100 bg-white pl-7 pr-2 text-xs text-gray-900 outline-none focus:border-blue-300"
                />
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-blue-100 bg-white">
                {options.map((product) => {
                    const isSelected = String(product.id) === String(selectedProductId);
                    const sku = String(product.supplySku || product.sku || "").trim();
                    const name = String(product.canonicalName || product.productName || product.name || sku).trim();
                    return (
                        <button
                            key={product.id}
                            type="button"
                            onClick={() => onSelect?.(product)}
                            className={`flex w-full items-center gap-2 border-b border-gray-100 px-2.5 py-2 text-left last:border-0 hover:bg-blue-50 ${
                                isSelected ? "bg-blue-50" : ""
                            }`}
                        >
                            <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                    isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                                }`}
                            >
                                {isSelected ? <CheckIcon className="h-3 w-3 text-white" /> : null}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-xs font-medium text-gray-900">{sku || name}</span>
                                <span className="block truncate text-[10px] text-gray-400">
                                    {name} · {product.components.length} components
                                </span>
                            </span>
                        </button>
                    );
                })}
                {!options.length ? <p className="px-3 py-4 text-center text-xs text-gray-400">No BOM sources found.</p> : null}
            </div>
        </div>
    );
}
